import { useCallback, useEffect, useRef, useState } from 'react';
import { useWalkthroughStore } from '../../store/walkthroughStore';
import { useDevStore } from '../../store/useDevStore';
import {
  areAutoToursEnabled,
  areToursEnabled,
  isTourDone,
  markTourDone,
  resetTourDone,
} from './tourPrefs';
import { capturePageMap } from './capturePageMap';
import { ensureTour, regenerateTour } from './tourApi';

const SIM_PROFILE = 'SIMULADOR';
const SIM_PATH = '/simulador/cockpit';
const SIM_TOUR_ID = 'page:/simulador/cockpit';

/**
 * Tour do ambiente SIMULADOR (chrome do cockpit).
 * Separado dos tours de perfil que rodam dentro dos iframes.
 */
export function useCockpitTourOnce() {
  const startWalkthrough = useWalkthroughStore((s) => s.startWalkthrough);
  const isActive = useWalkthroughStore((s) => s.isActive);
  const startedRef = useRef(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    const { settings } = useDevStore.getState();
    if (!settings.showTour || !settings.autoStartTour) return;
    if (isActive) return;
    if (!areAutoToursEnabled()) return;
    if (!areToursEnabled()) return;
    if (isTourDone(SIM_PROFILE, SIM_TOUR_ID)) return;
    if (startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      if (!areToursEnabled() || cancelled) return;
      if (useWalkthroughStore.getState().isActive) return;
      try {
        const capture = await capturePageMap({
          withScreenshot: false,
          ambiente: 'SIMULADOR',
        });
        if (cancelled) return;
        if (!capture.regions.length) return;

        const result = await ensureTour({
          perfil: SIM_PROFILE,
          urlPath: SIM_PATH,
          pageTitle: 'Simulador — Cockpit',
          regions: capture.regions,
          elements: capture.elements,
          contentHash: capture.contentHash,
          ambiente: 'SIMULADOR',
        });
        if (!result.passos?.length || cancelled) return;
        if (useWalkthroughStore.getState().isActive) return;
        startWalkthrough(result.passos);
        markTourDone(SIM_PROFILE, SIM_TOUR_ID);
      } catch {
        startedRef.current = false;
      }
    }, 1200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isActive, startWalkthrough]);

  const startNow = useCallback(async () => {
    resetTourDone(SIM_PROFILE, SIM_TOUR_ID);
    const capture = await capturePageMap({
      withScreenshot: false,
      ambiente: 'SIMULADOR',
    });
    const result = await ensureTour({
      perfil: SIM_PROFILE,
      urlPath: SIM_PATH,
      pageTitle: 'Simulador — Cockpit',
      regions: capture.regions,
      elements: capture.elements,
      contentHash: capture.contentHash,
      ambiente: 'SIMULADOR',
    });
    if (result.passos?.length) {
      startWalkthrough(result.passos);
      markTourDone(SIM_PROFILE, SIM_TOUR_ID);
    }
  }, [startWalkthrough]);

  const regenerateNow = useCallback(async () => {
    setRegenerating(true);
    try {
      resetTourDone(SIM_PROFILE, SIM_TOUR_ID);
      const capture = await capturePageMap({
        withScreenshot: true,
        ambiente: 'SIMULADOR',
      });
      const result = await regenerateTour({
        perfil: SIM_PROFILE,
        urlPath: SIM_PATH,
        pageTitle: 'Simulador — Cockpit',
        regions: capture.regions,
        elements: capture.elements,
        contentHash: capture.contentHash,
        screenshotBase64: capture.screenshotBase64,
        pageHtml: capture.pageHtml,
        ambiente: 'SIMULADOR',
      });
      if (result.passos?.length) {
        startWalkthrough(result.passos);
        markTourDone(SIM_PROFILE, SIM_TOUR_ID);
      }
    } finally {
      setRegenerating(false);
    }
  }, [startWalkthrough]);

  return { startNow, regenerateNow, regenerating };
}
